/* TM-006C: isolated cart recommendation shelf; no DOM observers */
(() => {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const ensureImagesLoad = (container) => {
    container.querySelectorAll('img').forEach((image) => {
      const source = image.dataset.src;
      const sourceSet = image.dataset.srcset;

      if (sourceSet && !image.getAttribute('srcset')) {
        image.setAttribute('srcset', sourceSet);
      }

      if (source && (!image.getAttribute('src') || image.naturalWidth === 0)) {
        image.setAttribute('src', source);
      }

      image.setAttribute('loading', 'eager');
    });
  };

  const onRefreshedQuantityClick = async (event) => {
    const adjust = event.currentTarget;
    if (adjust.disabled) return;

    const wrapper = adjust.closest('.js-qty__wrapper');
    const input = wrapper?.querySelector('.js-qty__num');
    const item = adjust.closest('.cart__item');
    const products = adjust.closest('[data-products]');
    if (!input?.dataset.id || !item || !products) return;

    const line = [...products.querySelectorAll('.cart__item')].indexOf(item) + 1;
    if (line < 1) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const current = Number(input.value || 0);
    const quantity = Math.max(0, current + (adjust.classList.contains('js-qty__adjust--plus') ? 1 : -1));
    input.value = quantity;
    wrapper.querySelectorAll('button').forEach((button) => { button.disabled = true; });

    try {
      const response = await fetch(ROOT + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ line, quantity })
      });

      if (!response.ok) throw new Error('Cart quantity update failed');
      await refreshDrawer();
    } catch (error) {
      input.value = current;
      wrapper.querySelectorAll('button').forEach((button) => { button.disabled = false; });
    }
  };

  const bindRefreshedQuantityControls = (container) => {
    container.querySelectorAll('.js-qty__adjust').forEach((button) => {
      button.addEventListener('click', onRefreshedQuantityClick, true);
    });
  };

  const refreshDrawer = async () => {
    const [markupResponse, cartResponse] = await Promise.all([
      fetch(ROOT + 'cart?view=ajax', {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }),
      fetch(ROOT + 'cart.js', { credentials: 'same-origin' })
    ]);

    if (!markupResponse.ok || !cartResponse.ok) throw new Error('Cart refresh failed');

    const template = document.createElement('template');
    template.innerHTML = (await markupResponse.text()).trim();
    const cart = await cartResponse.json();
    const form = document.getElementById('CartDrawerForm');
    const products = form?.querySelector('[data-products]');
    const incomingItems = template.content.querySelector('.cart__items');
    if (!form || !products || !incomingItems) return;

    const incomingDiscounts = template.content.querySelector('.cart__discounts');
    template.content.querySelectorAll('.cart__discounts').forEach((node) => node.remove());
    products.replaceChildren(...incomingItems.childNodes);
    products.dataset.ninjaCartRefreshed = 'true';
    ensureImagesLoad(products);
    bindRefreshedQuantityControls(products);

    const discounts = form.querySelector('[data-discounts]');
    if (discounts) discounts.replaceChildren(...(incomingDiscounts ? [incomingDiscounts] : []));

    const subtotal = form.querySelector('[data-subtotal]');
    if (subtotal && incomingItems.dataset.cartSubtotalFormatted) {
      subtotal.textContent = incomingItems.dataset.cartSubtotalFormatted;
    }

    document.querySelectorAll('[data-cart-count]').forEach((target) => {
      target.textContent = cart.item_count;
    });

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };

  class NinjaCartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.controller = new AbortController();
      this.addEventListener('change', this.onVariantChange);
      this.addEventListener('click', this.onClick);
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.removeEventListener('change', this.onVariantChange);
      this.removeEventListener('click', this.onClick);
    }

    onVariantChange = (event) => {
      const control = event.target.closest('[data-ninja-rec-variant]');
      if (!control) return;

      const card = control.closest('[data-ninja-recommendation-card]');
      const button = card?.querySelector('[data-ninja-recommendation-add]');
      if (!button) return;

      const option = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
      button.dataset.ninjaRecommendationAdd = option.value;
      if (option.dataset.label) button.textContent = option.dataset.label;
    };

    onClick = async (event) => {
      const button = event.target.closest('[data-ninja-recommendation-add]');
      if (!button || button.disabled) return;

      event.preventDefault();
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(ROOT + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: Number(button.dataset.ninjaRecommendationAdd), quantity: 1 }]
          })
        });

        if (!response.ok) throw new Error('Add to cart failed');
        button.textContent = button.dataset.labelAdded;
        await this.refreshCart();
      } catch (error) {
        if (error.name === 'AbortError') return;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    };

    async fetchIntent(intent) {
      const productId = this.dataset.productId;
      const url = ROOT + 'recommendations/products?section_id=ninja-cart-recommendations&limit=10&intent=' + intent + '&product_id=' + encodeURIComponent(productId);
      const response = await fetch(url, {
        credentials: 'same-origin',
        signal: this.controller.signal
      });

      if (!response.ok) return null;
      const template = document.createElement('template');
      template.innerHTML = (await response.text()).trim();
      return template.content;
    }

    async load() {
      if (!this.dataset.productId) return;

      try {
        const limit = Math.min(5, Math.max(1, Number(this.dataset.limit || 5)));
        const complementary = await this.fetchIntent('complementary');
        let related = null;
        let cards = complementary ? [...complementary.querySelectorAll('[data-ninja-recommendation-card]')] : [];

        if (cards.length < limit) {
          related = await this.fetchIntent('related');
          cards = cards.concat(related ? [...related.querySelectorAll('[data-ninja-recommendation-card]')] : []);
        }

        const uniqueCards = [];
        const productIds = new Set();
        for (const card of cards) {
          if (productIds.has(card.dataset.productId)) continue;
          productIds.add(card.dataset.productId);
          uniqueCards.push(card);
          if (uniqueCards.length >= limit) break;
        }

        if (!uniqueCards.length || !this.isConnected) return;

        const source = complementary?.querySelector('.ninja-cart-related') || related?.querySelector('.ninja-cart-related');
        if (!source) return;

        const list = source.querySelector('.ninja-cart-related__list');
        list.replaceChildren(...uniqueCards);
        this.replaceChildren(source);
        this.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') this.hidden = true;
      }
    }

    async refreshCart() {
      return refreshDrawer();
    }
  }

  if (!customElements.get('ninja-cart-recommendations')) {
    customElements.define('ninja-cart-recommendations', NinjaCartRecommendations);
  }
})();
